import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { buildQueryString } from 'src/lib/utilities/api/http';
import { mapResponse } from 'src/lib/utilities/api/map-response';
import { HelpDeskInfoModel } from './help-desk-info.model';
import {
	LoginProgramModel,
	LoginProgramSearchArgument,
} from './login-program.model';
import { LoginSsoOptionsModel } from './login-sso-options.model';
import { LoginStatusModel } from './login-status.model';
import { LoginModel } from './login.model';

@Injectable({
	providedIn: 'root',
})
export class LoginService {
	private logoutToken: string | null = null;

	constructor(private httpClient: HttpClient) {}

	private cacheLogoutToken = (logoutToken?: string | null): void => {
		if (logoutToken != null && logoutToken.trim() !== '') {
			this.logoutToken = logoutToken;
		}
	};

	public getLoginStatus = (): Observable<LoginStatusModel> => {
		return this.httpClient
			.get<LoginStatusModel>(`/api/v1/login_status?_format=json`)
			.pipe(
				mapResponse((r) => plainToInstance(LoginStatusModel, r), {
					errorCode: 'DD1C070C',
				}),
				tap((response) => {
					this.cacheLogoutToken(response?.logout_token);
				}),
			);
	};

	public login = (
		username: string,
		password: string,
	): Observable<LoginModel> => {
		return this.httpClient
			.post<LoginModel>(`/user/login?_format=json`, {
				name: username,
				pass: password,
			})
			.pipe(
				mapResponse((r) => plainToInstance(LoginModel, r), {
					errorCode: '86C90D5F',
					validateSuccess: false,
					checkPreMap: false,
				}),
				tap((response) => {
					this.cacheLogoutToken(response?.logout_token);
				}),
			);
	};

	public logout = (): Observable<unknown> => {
		const logoutToken$ =
			this.logoutToken != null
				? of(this.logoutToken)
				: this.getLoginStatus().pipe(
						map((response) => {
							if (
								response?.logout_token == null ||
								response.logout_token.trim() === ''
							) {
								throw new Error('Missing logout token for logout request');
							}
							return response.logout_token;
						}),
					);

		return logoutToken$.pipe(
			map((logoutToken) => {
				this.logoutToken = null;
				globalThis.location.assign(
					`/user/logout?token=${encodeURIComponent(logoutToken)}`,
				);
				return null;
			}),
		);
	};

	public getSsoOptions = (
		destination?: string,
		program_unique_id?: string,
	): Observable<LoginSsoOptionsModel> => {
		const qs = buildQueryString({
			program_unique_id,
			destination,
		});

		return this.httpClient.get<unknown>(`/api/v1/openid/sso${qs}`).pipe(
			mapResponse((r) => plainToInstance(LoginSsoOptionsModel, r), {
				errorCode: 'DD1C070C',
			}),
		);
	};

	public requestPasswordReset = (email: string): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/users/reset_password`, { email })
			.pipe(
				mapResponse((r) => r, {
					errorCode: '659A064E',
				}),
			);
	};

	public getHelpDeskInfo = (): Observable<HelpDeskInfoModel> => {
		return this.httpClient.get<LoginStatusModel>(`/api/v1/help_desk`).pipe(
			mapResponse((r) => plainToInstance(HelpDeskInfoModel, r), {
				errorCode: '2966F2FE',
			}),
		);
	};

	// POSTMAN: Anonymous Organizations Search
	public getPrograms = (
		args: Partial<LoginProgramSearchArgument>,
	): Observable<LoginProgramModel[]> => {
		return this.httpClient
			.post<{ results: unknown[] }>(`/api/v1/organizations/search/anonymous`, {
				filters: args,
			})
			.pipe(
				mapResponse((r) => plainToInstance(LoginProgramModel, r.results), {
					errorCode: 'E660FB97',
				}),
			);
	};
}
